import React, { useCallback, useId, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Card, ConfirmDialog, NotYetImplemented, TypeTag } from '../../components/ui';
import { deleteTemplate } from '../../api/templatesApi';
import { formatDateUTC } from '../../utils/dates';
import useLeaveGuard from '../../hooks/useLeaveGuard';
import { useAuth } from '../../context/useAuth';
import { useTask } from './useTask';
import StepGraph from './StepGraph';
import StepMenu from './StepMenu';
import { copyToCanvas } from './canvasCopy';
import { subgraphFrom } from './taskGraph';
import { readLastInputs } from './runInputs';
import { EMPTY_EDITS, applyEdits, countEdits, editsReducer } from './taskEdits';
import { ChangeModelDialog, EditInstructionsDialog, EditTaskDialog } from './EditDialogs';

const LINK = 'underline text-ink font-semibold focus-ring';
const LEAVE = 'Leave without saving your changes?';
const NONE = '—';

/** The last value shown for an input: a filename or the start of pasted text. */
function lastValueText(v) {
  if (v == null || v === '') return NONE;
  if (typeof v !== 'object') return String(v).slice(0, 40);
  if (v.source === 'paste') return v.text ? v.text.slice(0, 40) : NONE;
  return v.filename || NONE;
}

const sameTools = (a, b) => JSON.stringify(a || []) === JSON.stringify(b || []);

function Rows({ rows }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <dt className="text-pencil">{label}</dt>
          <dd className="font-semibold break-words min-w-0">{value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function SidePanel({ id, inputs }) {
  const userId = useAuth()?.user?.id;
  const needsId = useId();
  const lastId = useId();
  const last = readLastInputs(userId, id);
  return (
    <aside className="w-full lg:w-[320px] lg:shrink-0 flex flex-col gap-8">
      <section aria-labelledby={needsId} className="flex flex-col gap-3">
        <h2 id={needsId} className="font-display text-2xl font-extrabold">Each run needs</h2>
        <Rows rows={inputs.map((i) => [i.label, lastValueText(last?.values?.[i.name])])} />
      </section>
      <section aria-labelledby={lastId} className="flex flex-col gap-3">
        <NotYetImplemented feature="taskRuns">
          <h2 id={lastId} className="font-display text-2xl font-extrabold">Last run</h2>
        </NotYetImplemented>
        <Rows rows={[['When', NONE], ['Stopped at', NONE], ['Cost', NONE], ['Runs so far', NONE]]} />
      </section>
    </aside>
  );
}

function SavedTask({ id, task }) {
  const { template, graph, inputs } = task;
  const navigate = useNavigate();
  const [edits, dispatch] = useReducer(editsReducer, EMPTY_EDITS);
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState(null); // { kind: 'task' | 'instructions' | 'model', step? }
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [copyError, setCopyError] = useState(false);
  const copying = useRef(false);

  const count = countEdits(edits);
  useLeaveGuard(count > 0, LEAVE);

  const title = edits.task.name || template.template_name;
  const shown = useMemo(() => applyEdits(graph, edits), [graph, edits]);
  const stepCount = graph.steps.filter((s) => !s.isCheck).length;
  const closeDialog = useCallback(() => setDialog(null), []);

  const copy = async (g, source) => {
    if (copying.current) return;
    // Copying leaves the page, so unsaved edits would be lost: ask first.
    if (count > 0 && !window.confirm(LEAVE)) return;
    copying.current = true;
    setCopyError(false);
    try {
      // The copy is of the saved task, so it takes the saved name too.
      const flowId = await copyToCanvas({ name: template.template_name, graph: g, source });
      navigate(`/canvas?flow=${encodeURIComponent(flowId)}`);
    } catch (e) {
      setCopyError(true);
    } finally {
      copying.current = false;
    }
  };

  const editStep = (step, field, value) => dispatch({ type: 'editStep', key: step.key, field, value });

  const saveTask = ({ name, description }) => {
    if (name !== (edits.task.name ?? template.template_name)) dispatch({ type: 'editTask', field: 'name', value: name });
    if (description !== (edits.task.description ?? template.description ?? '')) {
      dispatch({ type: 'editTask', field: 'description', value: description });
    }
    setDialog(null);
  };
  const saveInstructions = (prompt) => {
    if (prompt !== dialog.step.prompt) editStep(dialog.step, 'prompt', prompt);
    setDialog(null);
  };
  const saveModel = ({ model, tools }) => {
    if (model !== dialog.step.model) editStep(dialog.step, 'model', model);
    if (!sameTools(tools, dialog.step.tools)) editStep(dialog.step, 'tools', tools);
    setDialog(null);
  };

  const onStepClick = (step, e) => {
    if (!e.shiftKey) {
      setSelected([]);
      return;
    }
    setSelected((s) => (s.includes(step.key) ? s.filter((k) => k !== step.key) : [...s, step.key]));
  };

  const confirmDelete = async () => {
    setDeleteError(null);
    try {
      await deleteTemplate(id);
    } catch (e) {
      setDeleteError("Couldn't delete this task.");
      return;
    }
    setDeleteOpen(false);
    navigate('/tasks');
  };
  const cancelDelete = useCallback(() => {
    setDeleteOpen(false);
    setDeleteError(null);
  }, []);

  const leadTitle = inputs.length === 1 ? inputs[0].label : `${inputs.length} inputs`;
  const lead = inputs.length > 0 ? (
    <Card as="article" variant="static" aria-label={`Input: ${leadTitle}`} className="p-3 border-dashed">
      <div className="flex items-center gap-2 flex-wrap">
        <TypeTag type="INPUT" />
        <span className="font-sans font-bold text-ink">{leadTitle}</span>
      </div>
      <p className="mt-1 text-sm text-pencil">you add each run</p>
    </Card>
  ) : undefined;

  const tagsFor = (s) => [s.edited && 'EDITED', s.deleted && 'DELETED'].filter(Boolean);
  const renderActions = (step) => (
    // Menu clicks must not reach the card, where a plain click clears the selection.
    <span onClick={(e) => e.stopPropagation()}>
      <StepMenu
        step={step}
        onEditInstructions={(s) => setDialog({ kind: 'instructions', step: s })}
        onChangeModel={(s) => setDialog({ kind: 'model', step: s })}
        onCopyFromHere={(s) => copy(subgraphFrom(graph, s.key), { kind: 'task', templateId: id, fromStep: s.key })}
        onDelete={(s) => dispatch({ type: 'deleteStep', key: s.key })}
      />
    </span>
  );

  return (
    <div className="bg-paper text-ink min-h-full">
      <div className="max-w-6xl mx-auto px-6 py-9 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <nav aria-label="Breadcrumb" className="text-sm text-pencil flex gap-1.5">
            <Link to="/tasks" className={LINK}>Tasks</Link>
            <span aria-hidden="true">/</span>
          </nav>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex flex-col gap-1 min-w-0">
              <h1 className="font-display text-4xl font-extrabold tracking-tight">{title}</h1>
              <p className="text-sm text-pencil">
                {`${stepCount} ${stepCount === 1 ? 'step' : 'steps'} · saved ${formatDateUTC(template.created_at)}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button asChild variant="primary">
                <Link to={`/tasks/${encodeURIComponent(id)}/run`}>Run</Link>
              </Button>
              <Button variant="secondary" onClick={() => setDialog({ kind: 'task' })}>Edit</Button>
              <Button
                variant="secondary"
                onClick={() => copy(graph, { kind: 'task', templateId: id })}
              >
                Copy to canvas
              </Button>
              <Button variant="secondary" onClick={() => setDeleteOpen(true)}>Delete</Button>
            </div>
          </div>
          {copyError && <div role="alert" className="text-sm text-status-failed">Couldn't copy to the canvas.</div>}
        </header>

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <div className="flex-1 min-w-0 flex flex-col gap-4 w-full">
            {count > 0 && (
              <div role="status" className="flex items-center gap-3 flex-wrap border-1.5 border-ink rounded-[14px] bg-hl-soft px-4 py-2">
                <span className="font-semibold">{count === 1 ? '1 unsaved change' : `${count} unsaved changes`}</span>
                <div className="ml-auto flex items-center gap-2">
                  <NotYetImplemented inline feature="taskEdits">
                    <Button variant="secondary" size="sm">Save</Button>
                  </NotYetImplemented>
                  <Button variant="secondary" size="sm" onClick={() => dispatch({ type: 'discard' })}>Discard</Button>
                </div>
              </div>
            )}
            <div>
              <NotYetImplemented inline feature="saveStepsAsTask" className="inline-block">
                <Button
                  variant="secondary"
                  size="sm"
                  title="Shift-click steps to pick them"
                  disabled={selected.length < 2}
                >
                  Save selection as a task
                </Button>
              </NotYetImplemented>
            </div>
            <StepGraph
              graph={shown}
              lead={lead}
              selectedKeys={selected}
              onStepClick={onStepClick}
              renderActions={renderActions}
              tagsFor={tagsFor}
            />
          </div>
          <SidePanel id={id} inputs={inputs} />
        </div>
      </div>

      <EditTaskDialog
        open={dialog?.kind === 'task'}
        name={edits.task.name ?? template.template_name}
        description={edits.task.description ?? template.description ?? ''}
        onSave={saveTask}
        onCancel={closeDialog}
      />
      <EditInstructionsDialog
        open={dialog?.kind === 'instructions'}
        step={dialog?.step}
        onSave={saveInstructions}
        onCancel={closeDialog}
      />
      <ChangeModelDialog
        open={dialog?.kind === 'model'}
        step={dialog?.step}
        onSave={saveModel}
        onCancel={closeDialog}
      />
      <ConfirmDialog
        open={deleteOpen}
        title={`Delete ${template.template_name}?`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        error={deleteError}
      />
    </div>
  );
}

export default function SavedTaskPage() {
  const { id } = useParams();
  const task = useTask(id);
  if (task.loading) return <div className="p-9 text-pencil">Loading…</div>;
  if (task.error || !task.template) {
    return (
      <div className="p-9 flex items-center gap-3">
        <p role="alert" className="text-status-failed">Couldn't open this task.</p>
        <Link to="/tasks" className={LINK}>Tasks</Link>
      </div>
    );
  }
  return <SavedTask key={id} id={id} task={task} />;
}
